import { Button, Tooltip } from "@material-ui/core";
import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import { FeeStatus, IdentificationStatus, RegistrationStatus } from "../../Base/AdoptionConstants";
import PaginatedTableResults from "../../Base/Components/PaginatedTableResults";
import { searchRegistrations } from "../../Base/Services/RegistrationService";
import useAdminContext from "../AdminContextProvider";

export default function RegistrationSearchResults({ inputSearchCriteria, defaultRowsPerPage, selectedRegistrationId }) {
  const adminContext = useAdminContext();

  async function getMatchIfExistsThenOpenCompare(registrationOne, registrationTwo) {
    adminContext.openDetailsForComparingRegistrations(registrationOne, registrationTwo);
  }

  function searchRegistrationsWithoutId(criteria) {
    if (selectedRegistrationId) {
      return searchRegistrations({ ...criteria, excludeRegistrationId: selectedRegistrationId });
    }
    return searchRegistrations(criteria);
  }

  const tableHeaders = [
    {
      headerName: "ID",
      getField: (row) => row.userFriendlyId,
      sortBy: "userFriendlyId",
    },
    {
      headerName: "Registration Date",
      getField: (row) => {
        let timeFromNow = moment(row.createdDate).fromNow();
        timeFromNow = timeFromNow.charAt(0).toUpperCase() + timeFromNow.slice(1);
        return (
          <Tooltip title={moment(row.createdDate).format("LLL")}>
            <span>{timeFromNow}</span>
          </Tooltip>
        );
      },
      sortBy: "createdDate",
    },
    {
      headerName: "Registrant",
      getField: (row) => {
        return `${row.registrant.contactInformation.lastName}, ${row.registrant.contactInformation.firstName}`;
      },
      sortBy:
        "registrant.contactInformation.lastName,registrant.contactInformation.firstName,registrant.contactInformation.middleName",
    },
    {
      headerName: "Adoptee Birth Date",
      getField: (row) =>
        row.birthDate
          ? moment(row.birthDate).format("LL")
          : row.birthDateRangeStart && row.birthDateRangeEnd
          ? moment(row.birthDateRangeStart).format("LL") + " - " + moment(row.birthDateRangeEnd).format("LL")
          : "-",
      sortBy: "birthDate,birthDateRangeStart,birthDateRangeEnd",
    },
    {
      headerName: "Actions",
      getField: (row) => (
        <>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              adminContext.openDetailsForUserFriendlyRegistrationId(row.userFriendlyId);
            }}
          >
            View Details
          </Button>
          {row.identificationStatus === IdentificationStatus.VERIFIED.value &&
            [FeeStatus.FEE_WAIVED.value, FeeStatus.FEE_PAID.value].includes(row.feeStatus) &&
            [RegistrationStatus.REGISTERED.value, RegistrationStatus.NEEDS_RESEARCH.value].includes(
              row.registrationStatus
            ) &&
            // Don't show the "find match" button if already finding a match
            !adminContext.selectedRegistration && (
              <span style={{ marginLeft: "5px" }}>
                <Button
                  to="/admin/search"
                  component={Link}
                  variant="contained"
                  color="default"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    adminContext.setSelectedRegistration(row);
                  }}
                >
                  Find Match
                </Button>
              </span>
            )}
          {row.identificationStatus !== "VERIFIED" &&
            // Don't show the "verify identity" button if finding a match
            !adminContext.selectedRegistration && (
              <span style={{ marginLeft: "5px" }}>
                <Button
                  variant="contained"
                  color="default"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    adminContext.openIdentityForUserFriendlyRegistrationId(row.userFriendlyId);
                  }}
                >
                  Verify Identity
                </Button>
              </span>
            )}
          {[
            FeeStatus.FEE_PENDING.value,
            FeeStatus.PREV_PAID_PENDING.value,
            FeeStatus.UPDATE_FEE_PENDING.value,
          ].includes(row.feeStatus) &&
            // Don't show the "verify payment" button if finding a match
            !adminContext.selectedRegistration && (
              <span style={{ marginLeft: "5px" }}>
                <Button
                  variant="contained"
                  color="default"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    adminContext.openPaymentForUserFriendlyRegistrationId(row.userFriendlyId);
                  }}
                >
                  Verify Payment
                </Button>
              </span>
            )}
          {adminContext.selectedRegistration !== null && (
            // Don't show the "verify payment" button if finding a match
            <span style={{ marginLeft: "5px" }}>
              <Button
                variant="contained"
                color="default"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  getMatchIfExistsThenOpenCompare(adminContext.selectedRegistration, row);
                }}
              >
                Compare
              </Button>
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <PaginatedTableResults
      inputSearchCriteria={inputSearchCriteria}
      tableColumns={tableHeaders}
      getTableResultsFunction={searchRegistrationsWithoutId}
      defaultRowsPerPage={defaultRowsPerPage}
      onRowClick={(row) => adminContext.openDetailsForUserFriendlyRegistrationId(row.userFriendlyId)}
      isRowSelected={(row) => adminContext.selectedRegistration?.registrationId === row?.registrationId}
    />
  );
}
